const {
  getLabels,
  readyChartData,
} = require("../../../../../utils/daily-stats");
const _ = require("lodash");
import { round } from "lodash";
import moment from "moment";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  bodyWeightFormat,
  calculateAverage,
  calculateBodyWeightChanges,
  filterByWeekRange,
  filterZeroValue,
  getPreviousWeek,
  mapBodyWeight,
  mapBodyWeights,
  sortByUtcDate,
} from "../body-composition.helper";
import AreaGraph from "./AreaGraph";
import NoData from "./NoData";
import SvgIcon from "../../../../../parts/SvgIcon";

const getGainOrLoss = (value, unit) => {
  const negativeValue = value < 0;
  return (
    <span className={!negativeValue ? "text-(--danger)" : "text-(--success)"}>
      <SvgIcon
        src={`/img/new-icons/${
          !negativeValue ? "red-arrow-up-rounded" : "green-arrow-down-rounded"
        }.svg`}
        noFilterOnLight={true}
      />
      <span>
        {" "}
        {value} {unit}
      </span>
    </span>
  );
};

const Title = ({ weightChange, unit }) => {
  const { t } = useTranslation("athlete-portal");
  return (
    <div>
      <h4>{t("bodyComposition.bodyweight.title")}</h4>
      <p className="p4">{t("bodyComposition.lastThreeMonths")}</p>
    </div>
  );
};

const LeftZone = ({ weightChange, threeMonthData, unit }) => {
  const { t } = useTranslation("athlete-portal");
  return (
    <div>
      {getGainOrLoss(weightChange, unit)}
      <div className="composition-bodyweight-graph">
        {threeMonthData.length ? (
          <div>
            <AreaGraph
              graphId={"body-weight-graph"}
              data={threeMonthData}
              targetValue={"bodyWeight"}
              label={t("bodyComposition.bodyweight.graphLabel")}
              description={t("bodyComposition.bodyweight.graphDescription")}
              units={unit}
            />
          </div>
        ) : (
          <NoData />
        )}
      </div>
    </div>
  );
};

const Item = ({ date = "", weight = "", unit = "" }) => (
  <tr>
    <td className="p4">{date}</td>
    <td className="s3">{parseInt(weight) ? `${weight} ${unit}` : "-"}</td>
  </tr>
);

const Results = ({ data, threeMonthData, start, end, unit }) => {
  const { t } = useTranslation("athlete-portal");
  const previousWeek = getPreviousWeek({ start, end });
  const filterData = threeMonthData
    .filter(
      (stat) =>
        filterByWeekRange(stat, previousWeek) &&
        filterZeroValue(stat.bodyWeight)
    )
    .sort(sortByUtcDate);
  const previousWeekAverage = calculateAverage(
    filterData.length ? filterData.map(mapBodyWeight) : [0]
  );

  const average = calculateAverage(
    data.map(mapBodyWeight).filter(filterZeroValue)
  );

  let difference = 0;

  const correctAverage = average ? average : 0;
  const correctPreviousWeekAverage = previousWeekAverage
    ? previousWeekAverage
    : 0;

  difference = round(correctAverage - correctPreviousWeekAverage, 1);

  return (
    <footer>
      <div>
        <p className="s3">{t("bodyComposition.bodyweight.average")}</p>

        {difference ? getGainOrLoss(difference, unit) : ""}

        {average ? (
          <span>
            {average} {unit}
          </span>
        ) : (
          "-"
        )}
      </div>
      <div>
        <p className="s3">{t("bodyComposition.bodyweight.lastWeek")}</p>
        <span>
          {previousWeekAverage ? (
            <span>
              {previousWeekAverage} {unit}
            </span>
          ) : (
            "-"
          )}
        </span>
      </div>
    </footer>
  );
};
const Table = ({
  data,
  threeMonthData,
  start,
  end,
  unit,
  date,
  athlete,
  fromCheckIn,
  organizationCoachParam,
}) => {
  const { t } = useTranslation("athlete-portal");
  return (
  <div className="composition-bodyweight-results-container">
    <section role="table-wrapper">
      <table role="report-table">
        <thead>
          <tr>
            <th>{t("bodyComposition.bodyweight.tableDate")}</th>
            <th>{t("bodyComposition.bodyweight.tableWeight")}</th>
          </tr>
        </thead>
        {data.map((bodyweight, idx) => (
          <Item
            key={idx}
            date={bodyweight.date}
            weight={bodyweight.bodyWeight}
            unit={unit}
          />
        ))}
      </table>
    </section>
    <Results
      data={data}
      threeMonthData={threeMonthData}
      start={start}
      end={end}
      unit={unit}
    />
    <div className="see-more-container">
      <SeeMoreComponent
        date={date}
        athlete={athlete}
        fromCheckIn={fromCheckIn}
        isDesktop={false}
        organizationCoachParam={organizationCoachParam}
      />
    </div>
  </div>
  );
};

const BodyWeightStats = ({
  dailyStats,
  athlete,
  date,
  threeMonthData,
  start,
  end,
  units,
  timePeriod = 7,
  organizationCoachParam,
  fromCheckIn = false,
}) => {
  const unitMultiplier = units === "metric" ? 1 : 2.205;
  const unitLabel = units === "metric" ? "Kg" : "Lbs";

  const data = dailyStats.length
    ? dailyStats.map((data) => mapBodyWeights(data, unitMultiplier))
    : [];

  const threeMonthDataBodyweight = threeMonthData.map((data) =>
    mapBodyWeights(data, unitMultiplier)
  );

  const defaultDates = Array.apply(null, Array(timePeriod)).map((_, i) => {
    return moment(start).add(i, "days").format(bodyWeightFormat);
  });

  const filledArray = [];

  const mapDays = data.reduce((map, obj) => {
    map[obj.date] = obj.bodyWeight;
    return map;
  }, {});

  for (let date of defaultDates) {
    const formattedDate = date;
    const bodyWeight = mapDays[date] || 0;
    const utcDate = Number(moment(date).format("YYYYMMDD"));
    filledArray.push({ date: formattedDate, bodyWeight, utcDate });
  }

  const threeMonthChange = calculateBodyWeightChanges(
    threeMonthDataBodyweight,
    unitMultiplier
  );

  return (
    <article id="bodyweight-comp">
      <header>
        <Title weightChange={threeMonthChange} unit={unitLabel} />
        <SeeMoreComponent
          date={date}
          athlete={athlete}
          fromCheckIn={fromCheckIn}
          isDesktop={true}
          organizationCoachParam={organizationCoachParam}
        />
      </header>

      <section>
        <LeftZone
          weightChange={threeMonthChange}
          threeMonthData={threeMonthDataBodyweight}
          unit={unitLabel}
        />
        <Table
          data={filledArray}
          threeMonthData={threeMonthDataBodyweight}
          start={start}
          end={end}
          unit={unitLabel}
          date={date}
          athlete={athlete}
          fromCheckIn={fromCheckIn}
          organizationCoachParam={organizationCoachParam}
        />
      </section>
    </article>
  );
};

export default BodyWeightStats;

const SeeMoreComponent = ({
  date,
  athlete,
  fromCheckIn,
  isDesktop,
  organizationCoachParam,
}) => {
  const { t } = useTranslation("athlete-portal");
  return (
    <a
      href={`
      /athlete-portal/metrics/bodyweight?date=${date}&athlete=${athlete}&bodycomp=true${organizationCoachParam}
      `}
      className={`composition-see-more ${
        isDesktop
          ? fromCheckIn
            ? "force-mobile-hidden"
            : ""
          : fromCheckIn
          ? "force-only-show-mobile"
          : ""
      }`}
    >
      <span>{t("bodyComposition.seeMore")}</span>
      <SvgIcon
        noFilterOnLight={true}
        src="/img/new-icons/arrow-square-right-gradient.svg"
      />
    </a>
  );
};
